import { useMemo, useState, useRef, useEffect } from "react";
import { Document, Page } from "react-pdf";
import "../../lib/pdfWorkerSetup";
import type { ProposalTheme } from "../../types/proposalPresentation";
import { themeStyle } from "../../lib/proposalPresentation";
import "../proposals/dealPresentation.css";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { Alert, Box, Button, Card, Chip, Stack, Typography } from "@mui/joy";

import {
  FilePdf,
  MapPin,
  CalendarBlank,
  ArrowSquareOut,
  Files,
} from "@phosphor-icons/react";

export default function InspectionReportView() {
  const { token } = useParams();
  const url = `/api/proposals/public/reports/${encodeURIComponent(token || "")}/`;
  const query = useQuery({
    queryKey: ["inspection-report", token],
    retry: false,
    queryFn: async () => {
      const response = await fetch(url, {
        credentials: "omit",
        referrerPolicy: "no-referrer",
      });
      if (!response.ok)
        throw new Error(
          "This report link is unavailable, expired, or revoked. Ask the inspection team for a current link.",
        );
      return response.json() as Promise<{
        organization: string;
        brand?: ProposalTheme;
        logo?: string;
        supporting_documents?: { title: string; category: string }[];
        title: string;
        property: string;
        version: number;
        inspection_date: string;
        findings: {
          id: number;
          title: string;
          location: string;
          notes: string;
          exception_reason: string;
        }[];
      }>;
    },
  });
  if (query.isError)
    return (
      <Alert color="warning" sx={{ m: 3 }}>
        {query.error.message}
      </Alert>
    );
  if (!query.data)
    return <Typography sx={{ p: 3 }}>Loading inspection report…</Typography>;
  const report = query.data;
  return (
    <Box
      className="deal-presentation"
      data-joy-color-scheme="light"
      style={report.brand ? themeStyle(report.brand, "light") : undefined}
      sx={{
        maxWidth: 1100,
        mx: "auto",
        p: { xs: 2, md: 5 },
        "--joy-palette-primary-solidBg": "var(--proposal-strong)",
        "--joy-palette-primary-softColor": "var(--proposal-accent)",
        "--joy-palette-primary-softBg": "var(--proposal-raised)",
      }}
    >
      {report.brand && (
        <link
          rel="stylesheet"
          href={`https://fonts.googleapis.com/css2?${[...new Set([report.brand.heading_font, report.brand.body_font])].map((font) => `family=${encodeURIComponent(font)}:wght@400;600;700;800`).join("&")}&display=swap`}
        />
      )}
      <Stack spacing={3}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
        >
          {report.logo ? (
            <img
              src={report.logo}
              alt={report.organization}
              style={{ maxWidth: 220, maxHeight: 70, objectFit: "contain" }}
            />
          ) : (
            <Typography level="title-md">{report.organization}</Typography>
          )}
          <Chip size="sm" variant="soft">
            Inspection report
          </Chip>
        </Stack>
        <Card sx={{ p: { xs: 3, md: 4 }, gap: 2.5 }}>
          <div>
            <Typography
              level="body-xs"
              sx={{
                color: "primary.softColor",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                mb: 1,
              }}
            >
              Your property inspection
            </Typography>
            <Typography level="h2" sx={{ letterSpacing: "-0.035em" }}>
              Inspection report
            </Typography>
          </div>
          <Typography level="title-lg" startDecorator={<MapPin size={20} />}>
            {report.property}
          </Typography>
          <Typography
            level="body-sm"
            startDecorator={<CalendarBlank size={18} />}
          >
            {report.inspection_date
              ? new Date(
                  `${report.inspection_date}T12:00:00`,
                ).toLocaleDateString("en-US", {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })
              : "Inspection date not provided"}{" "}
            · Version {report.version}
          </Typography>
          <Box
            sx={{ p: 2.5, borderRadius: "md", bgcolor: "background.level1" }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              gap={2}
              alignItems={{ sm: "center" }}
              justifyContent="space-between"
            >
              <div>
                <Typography
                  level="title-md"
                  startDecorator={<FilePdf size={21} />}
                >
                  The complete inspection record
                </Typography>
                <Typography level="body-sm" sx={{ mt: 0.5 }}>
                  All findings, photos and supporting documents in one PDF.
                </Typography>
              </div>
              <Button
                component="a"
                href={`${url}?download=1`}
                target="_blank"
                rel="noreferrer"
                endDecorator={<ArrowSquareOut />}
                sx={{ flexShrink: 0 }}
              >
                Open complete report
              </Button>
            </Stack>
          </Box>
        </Card>
        <Typography level="h4" component="h2">
          Inspection findings{" "}
          <Typography level="body-md">({report.findings.length})</Typography>
        </Typography>
        {report.findings.map((f, index) => (
          <Card key={f.id} sx={{ p: { xs: 2.5, md: 3 }, gap: 1.5 }}>
            <Stack direction="row" gap={2} alignItems="start">
              <Typography
                level="title-sm"
                sx={{
                  color: "primary.softColor",
                  bgcolor: "primary.softBg",
                  p: 1,
                  borderRadius: "sm",
                }}
              >
                {String(index + 1).padStart(2, "0")}
              </Typography>
              <div>
                <Typography level="title-lg">{f.title}</Typography>
                <Typography level="body-sm" sx={{ mt: 0.5 }}>
                  {f.location}
                </Typography>
              </div>
            </Stack>
            <Typography sx={{ whiteSpace: "pre-wrap", lineHeight: 1.8 }}>
              {f.notes ||
                f.exception_reason ||
                "See the complete report for details."}
            </Typography>
          </Card>
        ))}
        {!!report.supporting_documents?.length && (
          <Card sx={{ p: { xs: 2, md: 3 }, gap: 2 }}>
            <Typography
              level="h4"
              component="h2"
              startDecorator={<Files size={24} />}
            >
              Supporting documents
            </Typography>
            <Typography level="body-sm">
              Your inspection references are included in the complete report
              below.
            </Typography>
            <Stack spacing={1}>
              {report.supporting_documents.map((a, index) => (
                <Box
                  key={index}
                  sx={{
                    p: 1.5,
                    bgcolor: "background.level1",
                    borderRadius: "sm",
                  }}
                >
                  <Typography level="title-sm">{a.title}</Typography>
                  <Typography level="body-xs">
                    {a.category === "manufacturer"
                      ? "Manufacturer specs & warranty"
                      : "Building codes"}
                  </Typography>
                </Box>
              ))}
            </Stack>
            <CompleteReportPdf url={`${url}?download=1`} />
            <Button
              component="a"
              href={`${url}?download=1`}
              target="_blank"
              rel="noreferrer"
              variant="outlined"
              startDecorator={<FilePdf />}
              sx={{ alignSelf: "flex-start" }}
            >
              Open complete PDF
            </Button>
          </Card>
        )}
        <Typography level="body-xs" textAlign="center">
          Prepared by {report.organization} · Questions about these findings?
          Contact your inspection team.
        </Typography>
      </Stack>
    </Box>
  );
}

function CompleteReportPdf({ url }: { url: string }) {
  const [pages, setPages] = useState(0);
  const [page, setPage] = useState(1);
  const [width, setWidth] = useState(600);
  const ref = useRef<HTMLDivElement>(null);
  const source = useMemo(() => ({ url, withCredentials: false }), [url]);
  useEffect(() => {
    const observer = new ResizeObserver((entries) =>
      setWidth(Math.max(240, Math.floor(entries[0].contentRect.width))),
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <Box
      ref={ref}
      sx={{ minWidth: 0, overflow: "hidden", borderRadius: "md" }}
      aria-label="Complete inspection PDF"
    >
      <Stack
        direction="row"
        gap={1}
        alignItems="center"
        flexWrap="wrap"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Button
          size="sm"
          variant="soft"
          disabled={page <= 1}
          onClick={() => setPage((p) => p - 1)}
        >
          Previous
        </Button>
        <Typography level="body-sm">
          Page {page} of {pages || "…"}
        </Typography>
        <Button
          size="sm"
          variant="soft"
          disabled={page >= pages}
          onClick={() => setPage((p) => p + 1)}
        >
          Next
        </Button>
      </Stack>
      <Box
        sx={{
          maxHeight: "70vh",
          overflow: "auto",
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "white",
        }}
      >
        <Document
          file={source}
          onLoadSuccess={(pdf) => {
            setPages(pdf.numPages);
            setPage(1);
          }}
          error={
            <Alert color="warning">
              Unable to display the PDF. Use Open complete PDF to download the
              report.
            </Alert>
          }
        >
          <Page
            pageNumber={page}
            width={width}
            renderTextLayer={false}
            renderAnnotationLayer={false}
          />
        </Document>
      </Box>
    </Box>
  );
}
